import { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useLanguage } from "../../context/LanguageContext";
import CardIconActive from "../../assets/images/icons/Navigation/Card-active.png";
import CardIconInactive from "../../assets/images/icons/Navigation/Card-inactive.png";
import HomeIconActive from "../../assets/images/icons/Navigation/Home-active.png";
import HomeIconInactive from "../../assets/images/icons/Navigation/Home-inactive.png";
import CashbackIconActive from "../../assets/images/icons/Navigation/Cashback-active.png";
import CashbackIconInactive from "../../assets/images/icons/Navigation/Cashback-inactive.png";
import SignInIconActive from "../icons/SignInIconActive";
import SignInIconInactive from "../icons/SignInIconInactive";
import SignUpIconActive from "../icons/SignUpIconActive";
import SignUpIconInactive from "../icons/SignUpIconInactive";

export default function Navigation({ isAuthenticated = false }) {
  const [active, setActive] = useState("home");
  const navigate = useNavigate();
  const { tenanturlname } = useParams();
  const location = useLocation();
  const { t } = useLanguage();

  // Update active tab based on current route
  useEffect(() => {
    if (isAuthenticated) {
      // Authenticated user navigation
      if (location.pathname.includes('/myprofile')) {
        const searchParams = new URLSearchParams(location.search);
        const tab = searchParams.get('tab');
        if (tab === 'cashback') {
          setActive('cashback');
        } else if (tab === 'my-cards') {
          setActive('card');
        }
      } else if (location.pathname === `/${tenanturlname}` || location.pathname === `/${tenanturlname}/`) {
        setActive('home');
      }
    } else {
      // Guest user navigation
      if (location.pathname === `/${tenanturlname}/signin`) {
        setActive('signin');
      } else if (location.pathname === `/${tenanturlname}/create-account`) {
        setActive('signup');
      } else if (location.pathname === `/${tenanturlname}` || location.pathname === `/${tenanturlname}/`) {
        setActive('home');
      } else {
        // Default to home for other guest pages
        setActive('home');
      }
    }
  }, [location, tenanturlname, isAuthenticated]);

  // Define navigation paths for authenticated users
  const authenticatedTabs = [
    {
      id: "home",
      labelKey: "navigation.home",
      activeIcon: HomeIconActive,
      inactiveIcon: HomeIconInactive,
      path: `/${tenanturlname}`,
    },
    {
      id: "card",
      labelKey: "navigation.myCards",
      activeIcon: CardIconActive,
      inactiveIcon: CardIconInactive,
      path: `/${tenanturlname}/myprofile?tab=my-cards`,
    },
    {
      id: "cashback",
      labelKey: "navigation.cashback",
      activeIcon: CashbackIconActive,
      inactiveIcon: CashbackIconInactive,
      path: `/${tenanturlname}/myprofile?tab=cashback`,
    },
  ];

  // Define navigation paths for guest users
  const guestTabs = [
    {
      id: "home",
      labelKey: "navigation.home",
      activeIcon: HomeIconActive,
      inactiveIcon: HomeIconInactive,
      path: `/${tenanturlname}`,
    },
    {
      id: "signin",
      labelKey: "navigation.signIn",
      activeIcon: SignInIconActive,
      inactiveIcon: SignInIconInactive,
      path: `/${tenanturlname}/signin`,
    },
    {
      id: "signup",
      labelKey: "navigation.signUp",
      activeIcon: SignUpIconActive,
      inactiveIcon: SignUpIconInactive,
      path: `/${tenanturlname}/create-account`,
    },
  ];

  // Select tabs based on authentication status
  const tabs = isAuthenticated ? authenticatedTabs : guestTabs;

  const handleTabClick = (tab) => {
    setActive(tab.id);
    navigate(tab.path); // Navigate to the corresponding route
  };

  return (
    <div className="w-full sticky bottom-0 left-0 bg-white shadow-[0_-2px_10px_rgba(0,0,0,0.1)] z-50 md:hidden">
      {/* Container for tabs */}
      <div className="flex justify-around py-4 relative">
        {tabs.map((tab) => {
          const isActive = active === tab.id;
          const Icon = isActive ? tab.activeIcon : tab.inactiveIcon;
          const iconSrc = typeof Icon === 'string' ? Icon : Icon?.default;
          const label = t(tab.labelKey);

          return (
            <button
              key={tab.id}
              onClick={() => handleTabClick(tab)} // Use the new handler
              className="flex flex-col items-center relative flex-1"
            >
              {/* Active indicator line at the top */}
              {isActive && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2 w-16 h-1 bg-blue-600 rounded-full" />
              )}

              {iconSrc ? (
                <img
                  src={iconSrc}
                  alt={typeof label === 'string' ? label : tab.id}
                  className="w-6 h-6"
                />
              ) : typeof Icon === 'function' ? (
                <Icon />
              ) : null}

              <span
                className={`text-sm mt-1 ${
                  isActive ? "text-blue-600 font-semibold" : "text-gray-500"
                }`}
              >
                {typeof label === 'string' ? label : tab.id}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
